<template>
  <v-container fluid class="mx-auto mb-8 mt-5">
    <SyncLicensesAlert v-if="loadings.sync" class="mx-5 mt-4 mb-4" />

    <v-card class="mx-5 mb-4" max-width="100%">
      <v-progress-linear
        :active="loading"
        indeterminate
        color="primary"
      ></v-progress-linear>
      <v-card-title
        class="grey--text text--darken-2 font-weight-medium text-body-1"
      >
        {{ $t("licensing.activation.title") }}
      </v-card-title>
      <v-card-text>
        <span>
          {{ $t("licensing.activation.description") }}
        </span>
        <div class="license-toggle-row">
          <v-switch
            color="accentSuite"
            inset
            hide-details
            class="license-toggle-switch"
            v-model="licenseLimiterLocalActive"
            :disabled="loading"
            @change="handleLicenseLimiterToggle"
            :aria-label="licenseLimiterLabel"
          />
          <span class="license-toggle-label">{{ licenseLimiterLabel }}</span>
        </div>
      </v-card-text>
    </v-card>

    <v-card class="mx-5 mb-4" max-width="100%">
      <v-data-table
        :items="displayedDomainLicenses"
        :headers="headers"
        :items-per-page="itemsPerPage"
        :loading="licenseLimiterLocalActive && loadingDomainLicenses"
        :footer-props="{
          'items-per-page-options': itemsOptions,
        }"
        :height="'calc(80vh - 250px)'"
        fixed-header
        :sort-by="sortBy"
        item-class="statusClass"
        style="max-height: auto"
      >
        <template v-slot:top>
          <v-row dense class="my-0 py-0 mt-2" align="center">
            <v-col class="flex-grow-0 flex-shrink-1 d-flex align-center">
              <v-card-title class="my-0 word-break text-no-wrap fill-height">
                {{ $t("licensing.title") }}
              </v-card-title>
            </v-col>
            <v-col class="flex-grow-0 flex-shrink-1"></v-col>
            <v-col
              v-if="licenseLimiterLocalActive"
              align="right"
              :class="`flex-grow-1 flex-shrink-0 mr-5 give-space ${
                $vuetify.breakpoint.xs
                  ? 'd-flex flex-column'
                  : 'd-flex align-center justify-end'
              }`"
            >
              <DomainSearchInput
                :value="searchDomain"
                :label="$t('licensing.search.label')"
                :placeholder="$t('licensing.search.label')"
                :disabled="loading || loadingDomainLicenses"
                @input-search="onDomainSearch"
              />
              <v-btn
                large
                v-if="!editing"
                elevation="0"
                :dark="!(loadingDomainLicenses || loading)"
                color="accent"
                @click="handleSyncDomainLicenses"
                :disabled="loading"
                outlined
              >
                <v-icon
                  left
                  size="20"
                  :class="`${loadings.sync ? 'icon-spinner' : ''}`"
                >
                  ph-arrows-clockwise
                </v-icon>
                <span>{{ $t("action.sync") }}</span>
              </v-btn>
              <v-btn
                v-if="editing"
                large
                elevation="0"
                :dark="loading ? false : true"
                color="accent"
                @click="cancelEdition"
                :disabled="loading"
                outlined
              >
                <v-icon left size="20">ph-x</v-icon>
                <span>{{ $t("action.cancel") }}</span>
              </v-btn>
              <v-btn
                v-if="editing"
                large
                elevation="0"
                :dark="loading || !hasPendingChanges ? false : true"
                color="accent"
                @click="saveChanges"
                :disabled="loading || !hasPendingChanges"
              >
                <v-icon left size="20">ph-fill ph-floppy-disk-back</v-icon>
                <span>{{ $t("action.save") }}</span>
              </v-btn>
              <v-btn
                v-if="!editing"
                large
                elevation="0"
                :dark="loading ? false : true"
                color="accent"
                @click="editItems"
                :disabled="loading"
              >
                <v-icon left size="20">ph-pencil-simple</v-icon>
                <span>{{ $t("action.edit") }}</span>
              </v-btn>
            </v-col>
          </v-row>
          <v-divider></v-divider>
        </template>
        <template v-if="editing" v-slot:body.prepend="{ headers }">
          <tr>
            <td :colspan="headers.length" class="px-4 alert-prepend-row">
              <div class="alert-prepend-wrapper">
                <Alert
                  class="alert-inline"
                  :prominent="false"
                  color="info"
                  dense
                >
                  {{ $t("licensing.unlimitedInputHint") }}
                </Alert>
              </div>
            </td>
          </tr>
        </template>
        <template v-slot:footer>
          <v-divider></v-divider>
        </template>
        <template v-slot:no-data>
          <div class="no-data-wrapper">
            <EmptyStateTableData
              v-if="!licenseLimiterLocalActive"
              class-styles="text-center"
              :title-key="$t('licensing.disabledDomainLicensing.title')"
              :subtitle-key="
                $t('licensing.disabledDomainLicensing.description')
              "
              icon-color="#5B36C3"
              :show-action="false"
            />
            <EmptyStateTableData
              v-else
              class-styles="text-center"
              :title-key="$t('licensing.noData.title')"
              :subtitle-key="$t('licensing.noData.description')"
              icon-color="#5B36C3"
              :show-action="false"
            />
          </div>
        </template>
        <template
          v-for="license in domainLicensesHeader"
          v-slot:[`item.${license.key}`]="{ item }"
        >
          <div :key="license.key">
            <div v-if="editing">
              <template v-if="item[license.key]">
                <div class="license-limit-editor">
                  <v-icon
                    v-if="isUsageOverLimit(item.domain, license)"
                    color="warning"
                    small
                    class="license-limit-warning"
                  >
                    ph-warning-circle
                  </v-icon>
                  <span class="license-used-value">
                    {{ formatUsedForEdit(item[license.key]) }}
                  </span>
                  <span class="license-divider">/</span>
                  <v-text-field
                    dense
                    hide-details
                    single-line
                    class="license-limit-input"
                    :value="getDraftLimit(item.domain, license)"
                    @input="onLimitInput(item.domain, license, $event)"
                    type="text"
                    inputmode="decimal"
                    pattern="\\*|\\d*"
                    text-align="center"
                  />
                </div>
              </template>
            </div>
            <LicenseUsageCell
              v-else
              :key="license.key"
              :usage="item[license.key]"
              :license-name="license.label"
            />
          </div>
        </template>
      </v-data-table>
    </v-card>

    <ConfigurationLicenseLimiterCard v-if="licenseLimiterLocalActive" />
  </v-container>
</template>

<script>
import DomainSearchInput from "@/components/general/DomainSearchInput.vue";
import EmptyStateTableData from "@/components/general/EmptyStateTableData.vue";
import ConfigurationLicenseLimiterCard from "@/components/licenses/ConfigurationLicenseLimiterCard.vue";
import LicenseUsageCell from "@/components/licenses/LicenseUsageCell.vue";
import SyncLicensesAlert from "@/components/licenses/SyncLicensesAlert.vue";
import {
  UNLIMITED,
  UNLIMITED_LABEL,
} from "@/helpers/variables/backendConstants";
import { paging, tableHeight } from "@/helpers/variables/tablesDefault";
import { mapActions, mapGetters, mapMutations } from "vuex";

export default {
  name: "licensing",

  components: {
    SyncLicensesAlert,
    DomainSearchInput,
    EmptyStateTableData,
    LicenseUsageCell,
    ConfigurationLicenseLimiterCard,
  },

  data() {
    return {
      tableHeight,
      itemsPerPage: paging.perPage,
      itemsOptions: paging.All,
      editing: false,
      loadings: {
        sync: false,
        fetchLicenses: false,
        saveChanges: false,
        updateLicenseLimiterSettings: false,
      },
      licenseLimiterLocalActive: false,
      draftLimits: {},
      sortBy: ["domain"],
      searchDomain: "",
      hasFetchedLicenses: false,
    };
  },
  computed: {
    ...mapGetters(["domainLicenses", "loadingDomainLicenses", "company"]),

    licenseLimiterLabel() {
      return this.licenseLimiterLocalActive
        ? this.$t("licensing.activation.enabled")
        : this.$t("licensing.activation.disabled");
    },

    licenseLimiterIsActive() {
      return !!this.company?.license_limiter_settings?.is_active;
    },

    domainLicensesHeader() {
      if (!this.licenseLimiterLocalActive) {
        return [];
      }
      return (this.domainLicenses.header_licenses || []).map((license) => ({
        label: license,
        key: this.formatLicenseKey(license),
      }));
    },

    licenseHeaderByKey() {
      return this.domainLicensesHeader.reduce((acc, license) => {
        acc[license.key] = license;
        return acc;
      }, {});
    },

    headers() {
      const baseHeaders = [
        {
          value: "domain",
          text: this.$t("licensing.headers.domain"),
          sortable: true,
          align: "start",
          width: 300,
        },
        {
          value: "total",
          text: this.$t("licensing.headers.total"),
          sortable: false,
          align: "start",
          width: 100,
        },
      ];

      const dynamicHeaders = this.domainLicensesHeader.map((license) => ({
        value: license.key,
        text: license.label,
        sortable: false,
        align: "start",
        width: 300,
      }));

      return [...baseHeaders, ...dynamicHeaders];
    },

    formattedDomainLicenses() {
      if (!this.licenseLimiterLocalActive) {
        return [];
      }
      const domains = this.domainLicenses.items || {};
      return Object.entries(domains).map(([domain, domainLicenses]) => {
        const total = domainLicenses.total || {};
        const row = {
          domain,
          total: this.formatUsage(total),
          statusClass: "d-default",
        };

        this.domainLicensesHeader.forEach((license) => {
          const usageData =
            domainLicenses[license.label] || domainLicenses[license.key];
          row[license.key] = usageData
            ? {
                used: usageData.used_count || 0,
                limit: usageData.limit_count ?? usageData.limit ?? 0,
                key: usageData.key,
              }
            : null;
        });

        return row;
      });
    },

    displayedDomainLicenses() {
      const term = (this.searchDomain || "").trim().toLowerCase();
      if (!term) {
        return this.formattedDomainLicenses;
      }
      return this.formattedDomainLicenses.filter((item) => {
        return (item.domain || "").toLowerCase().includes(term);
      });
    },

    loading() {
      return Object.values(this.loadings).some((value) => value);
    },

    hasPendingChanges() {
      return this.preparePatchPayload().length > 0;
    },
  },
  methods: {
    ...mapMutations(["setSnackBar"]),
    ...mapActions([
      "getDomainLicenses",
      "updateDomainLicensesLimit",
      "updateLicenseLimiterSettings",
      "syncCompanyLicenses",
    ]),

    async handleLicenseLimiterToggle(nextValue) {
      const previousValue = this.licenseLimiterIsActive;
      if (this.loadings.updateLicenseLimiterSettings) {
        return;
      }

      this.loadings = { ...this.loadings, updateLicenseLimiterSettings: true };
      try {
        await this.updateLicenseLimiterSettings({ is_active: !!nextValue });
        this.setSnackBar({
          message: this.$t("licensing.snackars.successSettings"),
          show: true,
          color: "success",
          timeout: 4000,
        });
        this.licenseLimiterLocalActive = this.licenseLimiterIsActive;
        if (this.licenseLimiterLocalActive) {
          await this.fetchDomainLicenses();
        } else {
          this.cancelEdition();
        }
      } catch (error) {
        this.licenseLimiterLocalActive = previousValue;
        this.setSnackBar({
          message: this.$t("licensing.snackars.errorSettings"),
          show: true,
          color: "error",
          timeout: 4000,
        });
      } finally {
        this.loadings = {
          ...this.loadings,
          updateLicenseLimiterSettings: false,
        };
      }
    },

    formatUsage(value) {
      if (!value) {
        return "";
      }
      const used = value.used_count || value.used || 0;
      const limitRaw =
        value.limit_count != null
          ? value.limit_count
          : value.limit != null
          ? value.limit
          : null;
      const isUnlimited = limitRaw === UNLIMITED;
      const limitLabel = isUnlimited ? UNLIMITED_LABEL : limitRaw ?? 0;
      if (!used && (!limitRaw || limitRaw === 0) && !isUnlimited) {
        return "";
      }
      return `${used} / ${limitLabel}`;
    },
    formatLicenseKey(license) {
      if (!license) {
        return "";
      }
      return license
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "_")
        .replace(/^_+|_+$/g, "");
    },

    limitValueToDisplay(limit) {
      if (limit === UNLIMITED) {
        return UNLIMITED_LABEL;
      }
      if (limit == null) {
        return "";
      }
      return String(limit);
    },

    isValidEditableLimit(value) {
      const raw = value == null ? "" : value.toString();
      const trimmed = raw.trim();
      if (trimmed === "" || trimmed === UNLIMITED_LABEL) {
        return true;
      }
      return /^\d+$/.test(trimmed);
    },

    sanitizeLimitInput(value) {
      if (value == null) {
        return "";
      }
      const trimmed = value.toString().trim();
      if (trimmed === UNLIMITED_LABEL) {
        return UNLIMITED_LABEL;
      }
      return trimmed.replace(/\D+/g, "");
    },

    parseEditableLimit(value) {
      const raw = value == null ? "" : value.toString().trim();
      if (raw === "") {
        return UNLIMITED;
      }
      if (raw === UNLIMITED_LABEL) {
        return UNLIMITED;
      }
      const parsed = parseInt(raw, 10);
      return Number.isNaN(parsed) ? null : parsed;
    },

    formatUsedForEdit(value) {
      if (value === null || value === undefined) {
        return "";
      }
      return value.used || value.used_count || 0;
    },

    getUsageData(domain, license) {
      const domainLicenses = this.domainLicenses.items?.[domain] || {};
      return (
        domainLicenses[license.label] || domainLicenses[license.key] || null
      );
    },

    getEffectiveLimit(domain, license) {
      const draft = this.draftLimits[domain]?.[license.key];
      if (draft && draft.limit !== undefined) {
        return this.parseEditableLimit(draft.limit);
      }
      const usage = this.getUsageData(domain, license);
      if (!usage) {
        return null;
      }
      return usage.limit_count ?? usage.limit ?? null;
    },

    isUsageOverLimit(domain, license) {
      const usage = this.getUsageData(domain, license);
      if (!usage) {
        return false;
      }
      const used = usage.used || usage.used_count || 0;
      const limit = this.getEffectiveLimit(domain, license);
      if (limit === null || limit === UNLIMITED) {
        return false;
      }
      return used > limit;
    },

    getDraftLimit(domain, license) {
      const draft = this.draftLimits[domain]?.[license.key];
      if (draft) {
        return draft.limit ?? "";
      }
      const usage = this.getUsageData(domain, license);
      return this.limitValueToDisplay(usage?.limit_count ?? usage?.limit);
    },

    onLimitInput(domain, license, value) {
      const usage = this.getUsageData(domain, license);
      const sanitized = this.sanitizeLimitInput(value);
      if (!usage?.key || !this.isValidEditableLimit(sanitized)) {
        return;
      }

      if (!this.draftLimits[domain]) {
        this.$set(this.draftLimits, domain, {});
      }

      this.$set(this.draftLimits[domain], license.key, {
        limit: sanitized,
        key: usage.key,
      });
    },

    preparePatchPayload() {
      const payload = [];
      Object.entries(this.draftLimits).forEach(([domain, licenses]) => {
        Object.entries(licenses).forEach(([licenseKey, change]) => {
          if (!change?.key) {
            return;
          }
          const parsedLimit = this.parseEditableLimit(change.limit);
          if (parsedLimit === null) {
            return;
          }
          const license = this.licenseHeaderByKey[licenseKey];
          if (!license) {
            return;
          }
          const usage = this.getUsageData(domain, license);
          if (!usage) {
            return;
          }
          const currentLimit = usage?.limit_count ?? usage?.limit ?? null;
          if (currentLimit === parsedLimit) {
            return;
          }
          payload.push({ key: change.key, limit_count: parsedLimit });
        });
      });
      return payload;
    },

    onDomainSearch(value) {
      this.searchDomain = (value || "").trim();
    },

    async fetchDomainLicenses() {
      this.loadings = { ...this.loadings, fetchLicenses: true };
      try {
        await this.getDomainLicenses();
        this.hasFetchedLicenses = true;
      } finally {
        this.loadings = { ...this.loadings, fetchLicenses: false };
      }
    },

    async handleSyncDomainLicenses() {
      this.loadings = { ...this.loadings, sync: true };
      try {
        await this.syncCompanyLicenses();
        await this.getDomainLicenses();
        this.hasFetchedLicenses = true;
      } finally {
        this.loadings = { ...this.loadings, sync: false };
      }
    },

    editItems() {
      if (!this.licenseLimiterLocalActive) {
        return;
      }
      this.sortBy = ["statusClass", "domain"];
      this.draftLimits = {};
      this.editing = true;
    },
    cancelEdition() {
      this.draftLimits = {};
      this.sortBy = ["domain"];
      this.editing = false;
    },

    async saveChanges() {
      const payload = this.preparePatchPayload();
      if (!payload.length) {
        this.cancelEdition();
        return;
      }
      this.loadings = { ...this.loadings, saveChanges: true };
      try {
        await this.updateDomainLicensesLimit(payload);
        this.setSnackBar({
          message: this.$t("licensing.snackars.successUpdateLimit"),
          show: true,
          color: "success",
          timeout: 4000,
        });
        await this.fetchDomainLicenses();
        this.cancelEdition();
      } catch (error) {
        this.setSnackBar({
          message: this.$t("licensing.snackars.errorUpdateLimit"),
          show: true,
          color: "error",
          timeout: 4000,
        });
      } finally {
        this.loadings = { ...this.loadings, saveChanges: false };
      }
    },
  },
  created() {
    this.licenseLimiterLocalActive = this.licenseLimiterIsActive;
    if (this.licenseLimiterLocalActive) {
      this.fetchDomainLicenses();
    }
  },

  watch: {
    licenseLimiterIsActive: {
      handler(value) {
        if (!this.loadings.updateLicenseLimiterSettings) {
          this.licenseLimiterLocalActive = !!value;
          if (!this.licenseLimiterLocalActive) {
            this.cancelEdition();
          } else if (!this.hasFetchedLicenses) {
            this.fetchDomainLicenses();
          }
        }
      },
      immediate: true,
    },
  },
};
</script>

<style>
.give-space > * {
  margin: 0 0 0 12px;
}

.give-space {
  flex-wrap: wrap;
  gap: 12px;
}
.d-default {
  background-color: transparent;
}

.license-limit-editor {
  display: flex;
  align-items: center;
  gap: 4px;
}
.license-limit-warning {
  margin-right: 2px;
}
.license-divider {
  color: rgba(0, 0, 0, 0.5);
  margin: 0 4px;
}
.license-limit-input {
  max-width: 60px;
  margin: 0;
  padding: 0;
}
.license-limit-input >>> .v-field__control,
.license-limit-input >>> .v-field__outline {
  display: flex;
  align-items: center;
  justify-content: center;
}
.license-used-value {
  color: rgba(0, 0, 0, 0.3);
  font-weight: bold;
}

.license-toggle-row {
  display: flex;
  align-items: center;
  margin-top: 24px;
}

.license-toggle-switch {
  margin: 0;
  padding: 0;
}

.license-toggle-switch .v-input__slot {
  margin: 0;
  align-items: center;
}

.license-toggle-label {
  color: rgba(0, 0, 0, 0.6);
}

.no-data-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 40vh;
}

.alert-inline {
  display: block;
  width: 100%;
  max-width: 100%;
}

.alert-prepend-row {
  padding-top: 12px;
}

.alert-prepend-wrapper {
  margin-top: 12px;
}
</style>
